import { useEffect, useRef, useState } from 'react';
import { VALID_PROP_FIELD_TYPES, isTemplateString } from '../../lib';
import { useDynamicEntityStoreContext, useDynamicEntityCMSDataContext } from '../../contexts';

import TextAreaInput from '../../../inputs/form-inputs/TextAreaInput';
import SwitchInput from '../../../inputs/form-inputs/SwitchInput';
import FormattedTextInput from '../../../inputs/form-inputs/FormattedTextInput';
import NumberInput from '../../../inputs/form-inputs/NumberInput';
import CategorySelectorInput from './inputs/CategorySelectorInput';

import RepeaterInput from './inputs/RepeaterInput';
import TemplatesInput from './inputs/TemplatesInput';
import FormsInput from './inputs/FormsInput';
import ModulesInput from './inputs/ModulesInput';
import ObjectInput from './inputs/ObjectInput';
import ImageInput from './inputs/ImageInput';
import InputWrapper from './inputs/InputWrapper';
import RangeInput from '../../../inputs/form-inputs/RangeInput';
import AssignedCategoryInput from './inputs/AssignedCategoryInput';

export default function PropField(props) {
  const {
    bs_col_width,
    name,
    type,
    options = [],
    isRequired, // TODO
    component,
    template_schema,
    template_item_label,
    object_schema,
    category_id,
    category_internal_build_name,
    allowMultipleOptions,
    rangeProps,
  } = props;

  const { categoriesLinked, isModule } = useDynamicEntityCMSDataContext();

  const updateIndividualContent = useDynamicEntityStoreContext((state) => state.updateIndividualContent);
  const removeDynamicPageModule = useDynamicEntityStoreContext((state) => state.removeDynamicPageModule);
  const addDynamicPageModule = useDynamicEntityStoreContext((state) => state.addDynamicPageModule);
  const elementBeingEditedName = useDynamicEntityStoreContext((state) => state.elementBeingEditedName);
  const componentBeingEditedIndex = useDynamicEntityStoreContext((state) => state.componentBeingEditedIndex);
  const assignedCategory = useDynamicEntityStoreContext((state) => state.data.assigned_category);

  const value = component.props[name];

  const [isFocused, setIsFocused] = useState(false);
  const elementRef = useRef(null);

  const handleChange = (newValue) => updateIndividualContent(component.id, ['props', name], newValue);

  const handleModulesChange = (event) => {
    handleChange(event.value);

    if (!event.value) {
      removeDynamicPageModule(name);
      return;
    }

    addDynamicPageModule(event.value, name);
  };

  const renderOptions = {
    string: () => {
      if (options && options.length) {
        return (
          <select className="form-select" value={value || ''} onChange={(e) => handleChange(e.target.value)}>
            <option value="" disabled>
              Select an option
            </option>
            {options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      return <TextAreaInput className="form-control text-area-fit" value={value || ''} onChange={(e) => handleChange(e.target.value)} rows={1} />;
    },
    boolean: () => <SwitchInput checked={!!value} onChange={(e) => handleChange(e.target.checked)} />,
    array: () => (
      <select className="form-select" value={JSON.stringify(value) || ''} onChange={(e) => handleChange(JSON.parse(e.target.value))}>
        <option value="" disabled>
          Select an option
        </option>
        {options.length ? (
          options.map((option, index) => (
            <option key={index} value={JSON.stringify(option.value)}>
              {option.label}
            </option>
          ))
        ) : (
          <option value="">No options found.</option>
        )}
      </select>
    ),
    number: () => <NumberInput value={value || 0} onChange={(e) => handleChange(Number(e.target.value))} />,
    formatted_text: () => <FormattedTextInput value={value || ''} onEditorChange={(value) => handleChange(value)} />,
    file: () => <ImageInput id={name} value={value} onChange={(value) => handleChange(value)} />,
    forms: () => <FormsInput {...{ value, handleFormsChange: (e) => handleChange(e.value) }} />,
    modules: () => <ModulesInput {...{ value, handleModulesChange }} />,
    object: () => <ObjectInput {...{ ancestors: ['props', name], component, value, object_schema }} />,
    template: () => <TemplatesInput {...{ ancestors: ['props', name], component, onChange: handleChange, template_schema, template_item_label, value }} />,
    repeater: () => <RepeaterInput {...{ component, onChange: handleChange, value }} />,
    range: () => <RangeInput {...rangeProps} {...{ value, onChange: handleChange }} />,
  };

  useEffect(() => {
    if (elementBeingEditedName === name) {
      setIsFocused(true);
      elementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsFocused(false);
  }, [elementBeingEditedName, componentBeingEditedIndex, name]);

  if (!name || !type) {
    return (
      <InputWrapper ref={elementRef} {...props} className="dp-prop-field col-12" label="INVALID PROP">
        <span>This prop does not have the required 'name' or 'type' field.</span>
      </InputWrapper>
    );
  }

  if (category_id || category_internal_build_name) {
    const category = categoriesLinked.find((c) => c.id === category_id || c.internal_build_name === category_internal_build_name);

    return (
      <InputWrapper ref={elementRef} className={`dp-prop-field ${bs_col_width ? `col-${bs_col_width}` : 'col-12'} ${isFocused ? 'focused' : ''}`} {...props}>
        {category ? <CategorySelectorInput {...{ value, onChange: handleChange, category, allowMultipleOptions }} /> : <span>Error: Category not found.</span>}
      </InputWrapper>
    );
  }

  if (isModule && type === 'modules') {
    return (
      <InputWrapper ref={elementRef} className="dp-prop-field col-12" {...props}>
        <span>You are not allowed to select another module within a module.</span>
      </InputWrapper>
    );
  }

  if (!VALID_PROP_FIELD_TYPES.includes(type)) {
    return (
      <InputWrapper ref={elementRef} className="dp-prop-field col-12" {...props}>
        <span>This prop is not configured properly.</span>
      </InputWrapper>
    );
  }

  const isAssigned = assignedCategory && isTemplateString(value);

  return (
    <InputWrapper
      ref={elementRef}
      allowSettings
      className={`dp-prop-field ${bs_col_width ? `col-${bs_col_width}` : 'col-12'} ${isFocused ? 'focused' : ''}`}
      {...props}
    >
      {isAssigned ? <AssignedCategoryInput addon="Assigned to" value={value} /> : renderOptions[type]()}
    </InputWrapper>
  );
}
